import { Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { SwitchButton } from '@components/core-ui/form-elements/buttons/Button/variations/SwitchButton';
import { Header } from '@components/Header';
import { HeaderSwitchWrapper } from '@components/Header/components/HeaderSwitchWrapper';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { getAmericanTime } from '@helpers/getAmericanTimezone';
import { useModalContext } from '@providers/ModalProvider';
import { useAPI } from '@providers/useAPI';
import { routes } from '@routes';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { organizationsKeys } from 'app/queries/organizations/organizationsKeys';
import { INVITATION_STATUSES } from 'app/types/enums/invitationStatuses';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router';

import { createFetchOrganizationInvitationsQueryFn } from '../../api/fetchOrganizationInvitations';
import { CellContent } from './styles/CellContent';
import { MainContent } from './styles/MainContent';
import { RolesCell } from './styles/RolesCell';

export const Content = () => {
  const navigate = useNavigate();
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const { openModal, closeModal } = useModalContext();
  const { id: routeId } = useParams<{ id: string }>();
  const id = routeId ?? '';

  const invitationsQuery = useQuery({
    queryKey: organizationsKeys.invitationsByOrg(id),
    queryFn: createFetchOrganizationInvitationsQueryFn(call),
    enabled: id !== '',
  });
  const users = invitationsQuery.data ?? [];
  const isLoading = invitationsQuery.isFetching;

  const openInviteUserModal = () => {
    openModal(ModalNamesEnum.AdminInviteUserModal, {
      organizationId: id,
      onSuccess: () => {
        closeModal();
        void queryClient.invalidateQueries({ queryKey: organizationsKeys.invitationsByOrg(id) });
      },
    });
  };

  return (
    <>
      <Helmet>
        <title>Organization Invitations list - AutoScript Admin</title>
      </Helmet>
      <Header
        leftSideContent={[
          <HeaderSwitchWrapper key="tabSwitcher">
            <SwitchButton
              onClick={() => {
                navigate(routes.adminOrganizationUsersList.make(id));
              }}
            >
              Members
            </SwitchButton>
            <SwitchButton
              active
              onClick={() => {
                navigate(routes.adminOrganizationInvitationsList.make(id));
              }}
            >
              Invitations
            </SwitchButton>
          </HeaderSwitchWrapper>,
        ]}
        title="Invitations"
        buttonLabel={'Invite Member'}
        buttonOnClick={() => openInviteUserModal()}
      />

      <LinearProgressLoader active={isLoading} />
      <MainContent>
        <TableWrapper>
          <Table>
            <Thead>
              <Tr>
                <Th>Created</Th>
                <Th>Email Address</Th>
                <Th>Teams</Th>
                <Th>Status</Th>
                <Th>Expiry Date</Th>
              </Tr>
            </Thead>
            <Tbody>
              {users.map((data) => (
                <Tr key={data.id}>
                  <Td>
                    <CellContent>{getAmericanTime(data.createdAt)}</CellContent>
                  </Td>
                  <Td>
                    <CellContent>{data.email}</CellContent>
                  </Td>
                  <Td>
                    <CellContent>
                      {(data?.Teams || []).map((team) => (
                        <RolesCell key={team.id}>{team.name}</RolesCell>
                      ))}
                    </CellContent>
                  </Td>
                  <Td>
                    <CellContent>{INVITATION_STATUSES[data.status as keyof typeof INVITATION_STATUSES]}</CellContent>
                  </Td>
                  <Td>
                    <CellContent>{getAmericanTime(data.expiry)}</CellContent>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableWrapper>
      </MainContent>
    </>
  );
};
